'use client';

import { Button } from '@zealot/ui/components/button';
import { Input } from '@zealot/ui/components/input';
import { Textarea } from '@zealot/ui/components/textarea';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const InputGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        'border-input bg-field',
        'has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-ring/30',
        'has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-destructive/20',
        'dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40',
        'h-9 rounded-lg border transition-colors',
        'has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot][aria-invalid=true]]:ring-2',
        'in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0',
        'group/input-group relative flex w-full min-w-0 flex-wrap items-center outline-none',
        'has-[>textarea]:h-auto has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-end]]:h-auto',
        'has-[>textarea]:items-start',
        className,
      )}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  'text-muted-foreground h-auto gap-1 py-2 text-xs/relaxed font-medium ' +
    'group-data-[disabled=true]/input-group:opacity-50 ' +
    '[&>svg:not([class*=size-])]:size-3.5 flex cursor-text items-center justify-center select-none',
  {
    variants: {
      align: {
        'inline-start': 'pl-2 has-[>button]:ml-[-0.275rem] has-[>kbd]:ml-[-0.275rem] order-first',
        'inline-end': 'pr-2 has-[>button]:mr-[-0.275rem] has-[>kbd]:mr-[-0.275rem] order-last',
        'block-start': 'px-2 pt-2 group-has-[>input]/input-group:pt-2 order-first w-full justify-start',
        'block-end': 'px-2 pb-2 group-has-[>input]/input-group:pb-2 order-last w-full justify-start',
      },
    },
    defaultVariants: { align: 'inline-start' },
  },
);

const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      // biome-ignore lint/suspicious/noExplicitAny: event type from shadcn pattern
      onClick={(e: any) => {
        if (e.target?.closest?.('button')) return;
        e.currentTarget?.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const inputGroupButtonVariants = cva('gap-2 rounded-md text-xs/relaxed shadow-none flex items-center', {
  variants: {
    size: {
      xs: "h-5 gap-1 rounded-sm px-1 [&>svg:not([class*='size-'])]:size-3",
      sm: '',
      'icon-xs': 'size-6 p-0 has-[>svg]:p-0',
      'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: { size: 'xs' },
});

const InputGroupButton = ({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'submit' | 'reset';
  }) => {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
};

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      className={cn(
        "flex items-center gap-2 text-muted-foreground text-xs/relaxed [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className,
      )}
      {...props}
    />
  );
};

const InputGroupInput = ({ className, ...props }: Omit<React.ComponentProps<'input'>, 'size'>) => {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        'flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent',
        className,
      )}
      {...props}
    />
  );
};

const InputGroupTextarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        'flex-1 resize-none rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 aria-invalid:ring-0 dark:bg-transparent',
        className,
      )}
      {...props}
    />
  );
};

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText, InputGroupTextarea };
